import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { 
  Status, 
  StatusCreate, 
  StatusUpdate, 
  StatusApiResponse, 
  StatusPagedResponse, 
  StatusListResponse,
  StatusCodeCheckResponse 
} from '../models/Status';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StatusService {
  private baseUrl: string;
  private authCode = '-Pf-epql89Yr1U7x-zQWCtok_i1LJS-1wAEQHILHn8DyAzFuVVvuxg==';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {
    this.baseUrl = environment.microservices?.config ?? environment.apiUrl;
  }

  private buildUrl(endpoint: string, params?: HttpParams): string {
    let url = `${this.baseUrl}${endpoint}`;
    const isLocalhost = this.baseUrl.includes('localhost');
    if (!isLocalhost) {
      const separator = endpoint.includes('?') ? '&' : '?';
      url += `${separator}code=${this.authCode}`;
    }
    if (params) {
      const paramString = params.toString();
      if (paramString) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}${paramString}`;
      }
    }
    return url;
  }

  /**
   * 1. Criar Status
   * POST /status
   */
  create(status: StatusCreate): Observable<Status> {
    const url = this.buildUrl('/status');
    return this.http.post<StatusApiResponse>(url, status).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Erro ao criar status');
      }),
      catchError(err => {
        this.errorService.showError(err.error?.message || 'Erro ao criar status');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<Status> {
    const url = this.buildUrl(`/status/${id}`);
    return this.http.get<StatusApiResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Status não encontrado');
      }),
      catchError(err => {
        if (err.status !== 404) {
          this.errorService.showError('Erro ao buscar status');
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * 3. Buscar Status por Código
   * GET /status/code/{code}
   */
  getByCode(code: string): Observable<Status> {
    const url = this.buildUrl(`/status/code/${code}`);
    return this.http.get<StatusApiResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Status não encontrado');
      }),
      catchError(err => {
        if (err.status !== 404) {
          this.errorService.showError('Erro ao buscar status por código');
        }
        return throwError(() => err);
      })
    );
  }

  getByEntity(entityType: string, activeOnly: boolean = false): Observable<Status[]> {
    let params = new HttpParams();
    if (activeOnly) {
      params = params.set('activeOnly', 'true');
    }
    const url = this.buildUrl(`/status/by-entity/${entityType}`, params);
    return this.http.get<StatusListResponse>(url).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        throw new Error('Erro ao buscar status por entidade');
      }),
      catchError(err => {
        this.errorService.showError('Erro ao buscar status por entidade');
        return throwError(() => err);
      })
    );
  }

  /**
   * 5. Buscar Status Paginado
   * GET /status/paged
   */
  getPaged(
    page: number = 1, 
    pageSize: number = 50, 
    entityType?: string
  ): Observable<StatusPagedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    
    if (entityType) {
      params = params.set('entityType', entityType);
    }

    const url = this.buildUrl('/status/paged', params);
    return this.http.get<StatusPagedResponse>(url).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar status paginados');
        return throwError(() => err);
      })
    );
  }

  getDefault(entityType: string): Observable<Status> {
    const url = this.buildUrl(`/status/entity/${entityType}/default`);
    return this.http.get<StatusApiResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error('Nenhum status padrão encontrado para a entidade');
      }),
      catchError(err => {
        if (err.status !== 404) {
          this.errorService.showError('Erro ao buscar status padrão');
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * 7. Ativar Status
   * PUT /status/{id}/activate
   */
  activate(id: number): Observable<Status> {
    const url = this.buildUrl(`/status/${id}/activate`);
    return this.http.put<StatusApiResponse>(url, {}).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Erro ao ativar status');
      }),
      catchError(err => {
        this.errorService.showError('Erro ao ativar status');
        return throwError(() => err);
      })
    );
  }

  deactivate(id: number): Observable<Status> {
    const url = this.buildUrl(`/status/${id}/deactivate`);
    return this.http.put<StatusApiResponse>(url, {}).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Erro ao desativar status');
      }),
      catchError(err => {
        this.errorService.showError('Erro ao desativar status');
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<any> {
    const url = this.buildUrl(`/status/${id}`);
    return this.http.delete<any>(url).pipe(
      map(response => response),
      catchError(err => {
        const errorMessage = err.error?.message || err.message || 'Erro ao deletar status';
        this.errorService.showError(errorMessage);
        return throwError(() => err);
      })
    );
  }

  checkCode(code: string): Observable<boolean> {
    const url = this.buildUrl(`/status/code-exists/${code}`);
    return this.http.get<StatusCodeCheckResponse>(url).pipe(
      map(response => {
        if (response.success) {
          return response.exists;
        }
        return false;
      }),
      catchError(err => {
        console.error('Erro ao verificar código:', err);
        return throwError(() => err);
      })
    );
  }

  update(id: number, status: StatusUpdate): Observable<Status> {
    const url = this.buildUrl(`/status/${id}`);
    return this.http.put<StatusApiResponse>(url, status).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Erro ao atualizar status');
      }),
      catchError(err => {
        this.errorService.showError('Erro ao atualizar status');
        return throwError(() => err);
      })
    );
  }

  getAll(): Observable<Status[]> {
    return this.getPaged(1, 1000).pipe(
      map(response => response.data)
    );
  }

  getActiveByEntity(entityType: string): Observable<Status[]> {
    return this.getByEntity(entityType, true);
  }

  toggleActive(status: Status): Observable<Status> {
    if (status.id === undefined) {
      return throwError(() => new Error('ID do status não encontrado'));
    }
    return status.isActive ? this.deactivate(status.id) : this.activate(status.id);
  }
}
